import React from 'react';
import { Link } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import { metricsApi, readingsApi, initiativesApi } from '../services/trackerApi';
import { peopleApi } from '../services/api';
import { PageHeader } from '../components/PageHeader';
import { type MetricStatus, METRIC_STATUS_COLOR, TIER_ICON, TIER_TONE, computeMetricStatus } from '../utils/format';
import { buildChildrenIndex, getRootInitiatives } from '../utils/initiativeHierarchy';

export function Sparkline({ values, color }: { values: number[]; color: string }) {
  if (values.length === 0) return <span className="np-sub" style={{ fontSize: 11 }}>No data</span>;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  return (
    <div className="np-sparkline">
      {values.slice(-10).map((v, i) => (
        <div key={i} className="np-bar" style={{ height: `${8 + ((v - min) / range) * 22}px`, background: color }}></div>
      ))}
    </div>
  );
}

/** Radial value-vs-target ring — used in place of the sparkline until there's enough history (3+ readings) for a trend to mean anything. */
export function MetricRing({ value, target, direction, color }: { value: number; target?: number; direction: string; color: string }) {
  const size = 30;
  const stroke = 3;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  let pct = 100;
  if (target != null && target !== 0) {
    pct = direction === 'lower' ? (target / value) * 100 : (value / target) * 100;
    pct = Math.max(0, Math.min(100, pct));
  }
  const dash = (pct / 100) * circumference;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--bs-border-color)" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={`${dash} ${circumference}`} strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8.5, fontWeight: 700, color: 'var(--bs-heading-color)' }}>
        {Math.round(value)}
      </span>
    </div>
  );
}

/** One metric row - shared by every group below (a Portfolio, a Program, or the "not yet
 *  linked" fallback) so the row itself never drifts between them. */
const MetricRow: React.FC<{ m: any; status: MetricStatus; readings: any[] }> = ({ m, status, readings }) => {
  const latest = readings[readings.length - 1]?.value;
  return (
    <Link
      to={`/tracker/catalog/metrics/${m.metricId}`}
      className="d-flex align-items-center justify-content-between py-2"
      style={{ borderBottom: '1px solid var(--bs-border-color)' }}
    >
      <div style={{ minWidth: 0 }}>
        <div className="d-flex align-items-center gap-1">
          <span className="dot" style={{ width: 7, height: 7, borderRadius: '50%', background: `var(--bs-${METRIC_STATUS_COLOR[status]})`, display: 'inline-block', flexShrink: 0 }}></span>
          <span className="text-truncate" style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--bs-heading-color)', maxWidth: 220 }}>{m.name}</span>
        </div>
        <div className="np-sub" style={{ fontSize: 11 }}>
          {latest != null ? `${latest} ${m.unit}` : 'No readings'}{m.target != null ? ` · target ${m.target}` : ''}
        </div>
      </div>
      {readings.length > 0 && readings.length < 3 ? (
        <MetricRing value={readings[readings.length - 1].value} target={m.target} direction={m.direction} color={`var(--bs-${METRIC_STATUS_COLOR[status]})`} />
      ) : (
        <Sparkline values={readings.map((r: any) => r.value)} color={`var(--bs-${METRIC_STATUS_COLOR[status]})`} />
      )}
    </Link>
  );
};

/** A group's own metric rows - no card of its own, just the list, so it can render inside
 *  either a top-level .np-card (a Portfolio) or a nested .np-row-card (a Program sitting
 *  inside its Portfolio's card body). */
const MetricList: React.FC<{
  metrics: any[];
  metricStatus: (m: any) => MetricStatus;
  readingsByMetric: Record<string, any[]>;
}> = ({ metrics, metricStatus, readingsByMetric }) => (
  <>
    {metrics.length === 0 && <p className="np-sub small mb-0"><i className="ri-line-chart-line me-1"></i>No metrics tracked yet.</p>}
    {metrics.map((m) => (
      <MetricRow key={m.metricId} m={m} status={metricStatus(m)} readings={readingsByMetric[m.metricId] || []} />
    ))}
  </>
);

/** A Portfolio's own card, with each direct-child Program nested inside it as a .np-row-card -
 *  the codebase's real "inset panel that sits inside a card body already" primitive (same one
 *  a wizard's repeating-row editor uses) - because Programs are a subset of their Portfolio,
 *  not a sibling of it. A standalone Program (no Portfolio parent) renders the same outer card
 *  with no nested panels, since it has none. */
const PortfolioGroupCard: React.FC<{
  initiative: any;
  ownMetrics: any[];
  programs: any[];
  metricsFor: (init: any) => any[];
  metricStatus: (m: any) => MetricStatus;
  readingsByMetric: Record<string, any[]>;
}> = ({ initiative, ownMetrics, programs, metricsFor, metricStatus, readingsByMetric }) => {
  const isOffTarget = (metrics: any[]) => metrics.some((m) => metricStatus(m) === 'off-target');
  const anyOffTarget = isOffTarget(ownMetrics) || programs.some((p) => isOffTarget(metricsFor(p)));
  return (
    <div className={`np-card mb-4 ${anyOffTarget ? 'np-card-accent-danger' : ''}`}>
      <div className="np-card-head d-flex align-items-center gap-2">
        <span className={`np-icon-badge np-tone-${TIER_TONE[initiative.tier]}`}><i className={TIER_ICON[initiative.tier]}></i></span>
        <Link to={`/tracker/initiatives/${initiative.initiativeId}`} className="fw-semibold text-truncate" style={{ color: 'var(--bs-heading-color)', fontSize: 15 }}>{initiative.name}</Link>
        {programs.length > 0 && (
          <span className="np-chip np-tone-neutral" style={{ marginLeft: 'auto' }}>{programs.length} program{programs.length === 1 ? '' : 's'}</span>
        )}
      </div>
      <div className="np-card-body">
        <MetricList metrics={ownMetrics} metricStatus={metricStatus} readingsByMetric={readingsByMetric} />
        {programs.map((program: any) => {
          const programMetrics = metricsFor(program);
          return (
            <div key={program.initiativeId} className={`np-row-card mt-3 mb-0 ${isOffTarget(programMetrics) ? 'np-row-card-accent-danger' : ''}`}>
              <div className="np-row-head">
                <span className={`np-icon-badge np-icon-badge-sm np-tone-${TIER_TONE[program.tier]}`}><i className={TIER_ICON[program.tier]}></i></span>
                <Link to={`/tracker/initiatives/${program.initiativeId}`} className="fw-semibold text-truncate" style={{ color: 'var(--bs-heading-color)', fontSize: 13 }}>{program.name}</Link>
              </div>
              <MetricList metrics={programMetrics} metricStatus={metricStatus} readingsByMetric={readingsByMetric} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const CatalogPage: React.FC = () => {
  const { data: metrics = [] } = useQuery({ queryKey: ['metrics'], queryFn: metricsApi.getAll });
  const { data: initiatives = [] } = useQuery({ queryKey: ['initiatives'], queryFn: initiativesApi.getAll });
  // Kept for MetricGroupCard owner display elsewhere if ever needed - not currently rendered
  // here, since a Portfolio/Program's owner already shows on its own initiative page.
  useQuery({ queryKey: ['people'], queryFn: peopleApi.getAll });

  const readingQueries = useQueries({
    queries: metrics.map((m: any) => ({
      queryKey: ['readings', m.metricId],
      queryFn: () => readingsApi.list(m.metricId),
      enabled: !!m.metricId,
    })),
  });
  const readingsByMetric: Record<string, any[]> = {};
  metrics.forEach((m: any, i: number) => {
    readingsByMetric[m.metricId] = (readingQueries[i]?.data || []).slice().sort((a: any, b: any) => a.date.localeCompare(b.date));
  });

  const metricStatus = (m: any): MetricStatus => computeMetricStatus(m, readingsByMetric[m.metricId] || []);

  const onTargetCount = metrics.filter((m: any) => metricStatus(m) === 'on-target').length;
  const offTargetCount = metrics.filter((m: any) => metricStatus(m) === 'off-target').length;
  const projectBornCount = metrics.filter((m: any) => m.origin === 'Project').length;

  // Metrics are created on the initiative that owns them and tracked via that initiative's own
  // targetMetricIds - the Service Catalog's old Domain -> Service structure was the only thing
  // that ever organised metrics, and nothing else in the app depends on it (confirmed: Domains/
  // Services are read in exactly three other places, all of them just another display of this
  // same metric data). Portfolio -> Program is the real, live structure now, so it's what this
  // page reflects - two levels only, matching "Send to Catalog" itself: a Project's own
  // not-yet-sent metrics stay on the Project's own Metrics tab, not duplicated here.
  const portfoliosAndPrograms = initiatives.filter((i: any) => i.tier === 'Portfolio' || i.tier === 'Program');
  const childrenOf = buildChildrenIndex(portfoliosAndPrograms);
  const roots = getRootInitiatives(portfoliosAndPrograms);
  const metricsFor = (init: any) => metrics.filter((m: any) => (init.targetMetricIds || []).includes(m.metricId));

  const linkedMetricIds = new Set(portfoliosAndPrograms.flatMap((i: any) => i.targetMetricIds || []));
  const unlinked = metrics.filter((m: any) => !linkedMetricIds.has(m.metricId));

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-archive-drawer-line"
        title="Service Catalog"
        subtitle="Every metric that exists, reflected under the Portfolio or Program tracking it."
      />

      <div className="np-grid np-grid-4">
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-primary"><i className="ri-stack-line"></i></span>
            <p className="np-stat-label">Programs tracked</p>
            <p className="np-stat-value np-tabular">{portfoliosAndPrograms.filter((i: any) => i.tier === 'Program').length}</p>
            <p className="np-stat-trend" style={{ color: 'var(--bs-secondary-color)' }}>
              across {portfoliosAndPrograms.filter((i: any) => i.tier === 'Portfolio').length} portfolio{portfoliosAndPrograms.filter((i: any) => i.tier === 'Portfolio').length === 1 ? '' : 's'}
            </p>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-success"><i className="ri-checkbox-circle-line"></i></span>
            <p className="np-stat-label">Metrics on target</p>
            <p className="np-stat-value np-tabular np-tone-success">{onTargetCount}<span style={{ fontSize: 15, color: 'var(--bs-body-color)', fontWeight: 500 }}> / {metrics.length}</span></p>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-danger"><i className="ri-alert-line"></i></span>
            <p className="np-stat-label">Needs attention</p>
            <p className={`np-stat-value np-tabular${offTargetCount > 0 ? ' np-tone-danger' : ''}`}>{offTargetCount}</p>
            <p className="np-stat-trend" style={{ color: 'var(--bs-secondary-color)' }}>off target this period</p>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-primary"><i className="ri-git-branch-line"></i></span>
            <p className="np-stat-label">Born from projects</p>
            <p className="np-stat-value np-tabular np-tone-primary">{projectBornCount}</p>
            <p className="np-stat-trend" style={{ color: 'var(--bs-secondary-color)' }}>of {metrics.length} total metrics</p>
          </div>
        </div>
      </div>

      {roots.map((portfolioOrStandaloneProgram: any) => (
        <PortfolioGroupCard
          key={portfolioOrStandaloneProgram.initiativeId}
          initiative={portfolioOrStandaloneProgram}
          ownMetrics={metricsFor(portfolioOrStandaloneProgram)}
          programs={childrenOf.get(portfolioOrStandaloneProgram.initiativeId) || []}
          metricsFor={metricsFor}
          metricStatus={metricStatus}
          readingsByMetric={readingsByMetric}
        />
      ))}
      {roots.length === 0 && (
        <div className="np-card mb-3">
          <div className="np-card-body text-center np-sub py-5">
            <i className="ri-stack-line d-block mb-2" style={{ fontSize: '2rem' }}></i>
            No Portfolios or Programs yet — metrics show up here once one exists to track them.
          </div>
        </div>
      )}

      {/* Metrics not (yet) in any Portfolio/Program's targetMetricIds - a Project's own metric
          before it's been sent, or a genuinely pre-existing one that predates this whole model. */}
      {unlinked.length > 0 && (
        <div className="np-card">
          <div className="np-card-head"><h2 className="mb-0">Not yet linked to a Portfolio or Program</h2></div>
          <div className="np-card-body pt-2">
            {unlinked.map((m: any) => {
              const origin = initiatives.find((i: any) => i.initiativeId === m.originInitiativeId);
              return (
                <div key={m.metricId} className="py-1">
                  <MetricRow m={m} status={metricStatus(m)} readings={readingsByMetric[m.metricId] || []} />
                  {origin && (
                    <div className="np-sub text-truncate" style={{ fontSize: 10.5, marginTop: '-0.4rem' }}>
                      <i className="ri-flashlight-line me-1"></i>{origin.name}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
